/**
 * Transform descriptor for custom column mappings: pattern / constant / valueMap.
 * Extracted into its own file (co-located logically with `ColumnMappingItemSchema`
 * in import-job.ts, which re-exports these symbols) to keep import-job.ts small.
 *
 * ReDoS safety bounds — see `import-mapping-transforms/spec.md` "Placeholder
 * pattern extraction":
 *  - pattern string <= PATTERN_MAX_LENGTH chars (enforced here via `.max()`)
 *  - matched cell value <= TRANSFORM_VALUE_MAX_LENGTH chars (enforced at apply
 *    time in `../import/transforms.ts#applyPattern` — raw XLSX cell values
 *    aren't Zod-validated before pattern matching).
 */
import { z } from 'zod';

export const PATTERN_MAX_LENGTH = 500;
export const TRANSFORM_VALUE_MAX_LENGTH = 10_000;
/**
 * Hard cap on the number of entries a `valueMap` transform's dictionary may
 * hold. `applyValueMap` linearly scans every entry per data row on both the
 * API dry-run and worker import paths, so an unbounded map multiplies cost
 * across every row — 200 comfortably covers real value-map vocabularies
 * (e.g. media-type or lighting enumerations) while bounding per-row cost.
 */
export const VALUE_MAP_MAX_ENTRIES = 200;

export const PatternTransformSchema = z.object({
  type: z.literal('pattern'),
  pattern: z.string().min(1).max(PATTERN_MAX_LENGTH),
  /** Per-placeholder target field key, in placeholder-number order ({1}, {2}, …). null = skip. */
  targets: z.array(z.string().nullable()).min(1),
});
export type PatternTransform = z.infer<typeof PatternTransformSchema>;

export const ConstantTransformSchema = z.object({
  type: z.literal('constant'),
  value: z.string(),
});
export type ConstantTransform = z.infer<typeof ConstantTransformSchema>;

export const ValueMapTransformSchema = z.object({
  type: z.literal('valueMap'),
  map: z
    .record(z.string(), z.string())
    .refine((map) => Object.keys(map).length <= VALUE_MAP_MAX_ENTRIES, {
      message: `map must have at most ${VALUE_MAP_MAX_ENTRIES} entries`,
    }),
});
export type ValueMapTransform = z.infer<typeof ValueMapTransformSchema>;

/**
 * Discriminated union on `type` — Zod rejects any value whose `type` is outside
 * `pattern | constant | valueMap` at `.parse()`/`.safeParse()` time.
 */
export const TransformDescriptorSchema = z.discriminatedUnion('type', [
  PatternTransformSchema,
  ConstantTransformSchema,
  ValueMapTransformSchema,
]);
export type TransformDescriptor = z.infer<typeof TransformDescriptorSchema>;

/**
 * Advisory pattern/target suggestion for one detected column, surfaced on
 * `ImportColumnsResponse`. Suggestions are never applied automatically — the
 * operator must confirm them (see spec.md "Pattern and transform suggestion
 * from samples").
 */
export const ColumnTransformSuggestionSchema = z.object({
  sourceColumn: z.string(),
  pattern: z.string(),
  targets: z.array(z.string().nullable()),
});
export type ColumnTransformSuggestion = z.infer<typeof ColumnTransformSuggestionSchema>;
